import { DateTime } from "luxon";

/**
 * Returns date with just time when the date is today otherwise it will return the date time.
 * If it is the same year it will return mm/dd hh:mm am/pm otherwise it will return the full date.
 */
export const smartDate = (date: string) => {
  const parsedDate = DateTime.fromISO(date);
  const now = DateTime.now();

  if (parsedDate.hasSame(now, "day")) {
    return parsedDate.toFormat("h:mm a");
  }

  if (parsedDate.hasSame(now, "year")) {
    return parsedDate.toFormat("MMM d");
  }

  return parsedDate.toFormat("MMM d, yyyy");
};

type DateGroup<T> = {
  groupId: string;
  label: string;
  items: T[];
};

type AnyRecord = Record<string, unknown>;

const getTimeSlots = () => {
  const now = DateTime.now();
  const startOfWeek = now.startOf("week");
  const startOfLastWeek = startOfWeek.minus({ weeks: 1 });
  const startOfMonth = now.startOf("month");

  return {
    now,
    startOfWeek,
    startOfLastWeek,
    startOfMonth,
  };
};

/**
 * Groups items by date into predefined time buckets.
 *
 * @example
 * const items = [
 *   { id: 1, createdAt: "2023-05-01T12:00:00Z", name: "Item 1" },
 *   { id: 2, createdAt: "2023-05-15T12:00:00Z", name: "Item 2" },
 *   { id: 3, createdAt: "2023-05-25T12:00:00Z", name: "Item 3" }
 * ];
 *
 * // Assuming today is 2023-05-26
 * const result = groupByDate(items, "createdAt");
 * // Returns:
 * // [
 * //   { label: "Recent", items: [{ id: 3, createdAt: "2023-05-25T12:00:00Z", name: "Item 3" }] },
 * //   { label: "Last Week", items: [{ id: 2, createdAt: "2023-05-15T12:00:00Z", name: "Item 2" }] },
 * //   { label: "Last Month", items: [{ id: 1, createdAt: "2023-05-01T12:00:00Z", name: "Item 1" }] },
 * //   { label: "Older", items: [] }
 * // ]
 */
export function groupByDate<T extends AnyRecord>(
  items: T[],
  dateKey: keyof T,
): DateGroup<T>[] {
  const { startOfWeek, startOfLastWeek, startOfMonth } = getTimeSlots();

  const groups: Record<string, T[]> = {
    "This Week": [],
    "Last Week": [],
    "Last Month": [],
    Older: [],
  };

  items.forEach((item) => {
    const dateValue = item[dateKey];
    if (!dateValue) {
      groups["Older"].push(item);
      return;
    }

    let date: DateTime;
    try {
      if (dateValue instanceof Date) {
        date = DateTime.fromJSDate(dateValue as Date);
      } else if (typeof dateValue === "string") {
        date = DateTime.fromISO(dateValue);
      } else {
        groups["Older"].push(item);
        return;
      }

      if (date >= startOfWeek) {
        groups["This Week"].push(item);
      } else if (date >= startOfLastWeek) {
        groups["Last Week"].push(item);
      } else if (date >= startOfMonth) {
        groups["Last Month"].push(item);
      } else {
        groups["Older"].push(item);
      }
    } catch {
      // If there's any error parsing the date, put it in the 'Older' bucket
      groups["Older"].push(item);
    }
  });

  // Sort each group by date in descending order
  Object.entries(groups).forEach(([key, groupItems]) => {
    if (groupItems.length > 0) {
      groups[key] = [...groupItems].sort((a, b) => {
        const dateA = new Date(String(a[dateKey])).getTime();
        const dateB = new Date(String(b[dateKey])).getTime();
        return dateB - dateA; // Sort in descending order (newest first)
      });
    }
  });

  // Filter out empty groups and return
  return Object.entries(groups)
    .filter(([, groupItems]) => groupItems.length > 0)
    .map(([label, groupItems]) => ({
      groupId: label,
      label,
      items: groupItems,
    }));
}
